(() => {
  const ROOT_SELECTOR =
    '[data-testimonial-wrap]';

  const ITEM_SELECTOR =
    '[data-testimonial-item]';

  const IMAGE_CLIP_HIDDEN =
    'circle(0% at 50% 50%)';

  const IMAGE_CLIP_VISIBLE =
    'circle(50% at 50% 50%)';

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let dependenciesRegistered = false;
  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function hasDependencies() {
    return Boolean(
      window.gsap &&
      window.SplitText
    );
  }

  function registerDependencies() {
    if (!hasDependencies()) {
      return false;
    }

    if (!dependenciesRegistered) {
      gsap.registerPlugin(
        SplitText
      );

      dependenciesRegistered = true;
    }

    return true;
  }


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getRoots(
    scope = document
  ) {
    const roots = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (
      typeof scope.querySelectorAll ===
      'function'
    ) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    return Array.from(
      new Set(roots)
    );
  }


  /* =========================================
     CREATE INSTANCE
     ========================================= */

  function createTestimonials(root) {
    if (!registerDependencies()) {
      return null;
    }

    if (
      root._wamboltTestimonials &&
      typeof root
        ._wamboltTestimonials
        .destroy === 'function'
    ) {
      root
        ._wamboltTestimonials
        .destroy();
    }

    const list =
      root.querySelector(
        '[data-testimonial-list]'
      );

    if (!list) {
      return null;
    }

    const items =
      Array.from(
        list.querySelectorAll(
          ITEM_SELECTOR
        )
      );

    if (!items.length) {
      return null;
    }

    const previousButton =
      root.querySelector(
        '[data-prev]'
      );

    const nextButton =
      root.querySelector(
        '[data-next]'
      );

    const autoplayButton =
      root.querySelector(
        '[data-autoplay-toggle]'
      );

    const currentElement =
      root.querySelector(
        '[data-current]'
      );

    const totalElement =
      root.querySelector(
        '[data-total]'
      );

    const controller =
      new AbortController();

    const { signal } =
      controller;

    const reducedMotion =
      reducedMotionQuery.matches;

    const autoplayEnabled =
      root.getAttribute(
        'data-autoplay'
      ) === 'true' &&
      items.length > 1 &&
      !reducedMotion;

    const parsedDuration =
      Number.parseInt(
        root.getAttribute(
          'data-autoplay-duration'
        ),
        10
      );

    const autoplayDuration =
      Number.isFinite(parsedDuration)
        ? Math.max(
            parsedDuration,
            1000
          )
        : 6000;

    let activeIndex =
      items.findIndex(
        (item) =>
          item.classList.contains(
            'is-active'
          )
      );

    if (activeIndex < 0) {
      activeIndex = 0;
    }

    let isAnimating = false;

    let isInView = true;

    let documentHidden =
      document.hidden;

    let autoplayPausedByUser =
      false;

    let autoplayTimer = null;

    let transitionTimeline = null;

    let intersectionObserver = null;

    let destroyed = false;

    const slides =
      items.map(
        (item) => ({
          item,

          image:
            item.querySelector(
              '[data-testimonial-img]'
            ),

          splitTargets: [
            item.querySelector(
              '[data-testimonial-text]'
            ),

            ...item.querySelectorAll(
              '[data-testimonial-split]'
            ),
          ].filter(Boolean),

          splitInstances: [],

          getLines() {
            return this
              .splitInstances
              .flatMap(
                (instance) =>
                  instance.lines || []
              );
          },
        })
      );


    /* =========================================
       COUNTER
       ========================================= */

    if (totalElement) {
      totalElement.textContent =
        String(
          items.length
        ).padStart(
          2,
          '0'
        );
    }

    function updateCounter() {
      if (!currentElement) {
        return;
      }

      currentElement.textContent =
        String(
          activeIndex + 1
        ).padStart(
          2,
          '0'
        );
    }


    /* =========================================
       SLIDE STATE
       ========================================= */

    function setSlideState(
      slideIndex,
      isActive
    ) {
      const slide =
        slides[slideIndex];

      if (!slide) {
        return;
      }

      const { item } =
        slide;

      item.classList.toggle(
        'is-active',
        isActive
      );

      item.setAttribute(
        'aria-hidden',
        String(!isActive)
      );

      item.toggleAttribute(
        'inert',
        !isActive
      );

      gsap.set(
        item,
        {
          autoAlpha:
            isActive
              ? 1
              : 0,

          pointerEvents:
            isActive
              ? 'auto'
              : 'none',
        }
      );
    }

    slides.forEach(
      (_, index) => {
        setSlideState(
          index,
          index === activeIndex
        );
      }
    );

    updateCounter();


    /* =========================================
       SPLITTEXT
       ========================================= */

    if (!reducedMotion) {
      slides.forEach(
        (
          slide,
          slideIndex
        ) => {
          slide.splitInstances =
            slide.splitTargets.map(
              (element) =>
                SplitText.create(
                  element,
                  {
                    type: 'lines',
                    mask: 'lines',
                    linesClass:
                      'text-line',
                    autoSplit: true,

                    onSplit(self) {
                      if (destroyed) {
                        return;
                      }

                      const isActive =
                        slideIndex ===
                        activeIndex;

                      gsap.set(
                        self.lines,
                        {
                          yPercent:
                            isActive
                              ? 0
                              : 110,
                        }
                      );

                      if (
                        slide.image
                      ) {
                        gsap.set(
                          slide.image,
                          {
                            clipPath:
                              isActive
                                ? IMAGE_CLIP_VISIBLE
                                : IMAGE_CLIP_HIDDEN,
                          }
                        );
                      }
                    },
                  }
                )
            );
        }
      );
    }


    /* =========================================
       AUTOPLAY TIMER
       ========================================= */

    function shouldPauseAutoplay() {
      return (
        !autoplayEnabled ||
        autoplayPausedByUser ||
        documentHidden ||
        !isInView ||
        isAnimating
      );
    }

    function clearAutoplayTimer() {
      if (
        autoplayTimer === null
      ) {
        return;
      }

      window.clearTimeout(
        autoplayTimer
      );

      autoplayTimer = null;
    }

    function scheduleAutoplay() {
      clearAutoplayTimer();

      if (
        destroyed ||
        shouldPauseAutoplay()
      ) {
        return;
      }

      autoplayTimer =
        window.setTimeout(
          () => {
            autoplayTimer = null;

            if (
              destroyed ||
              shouldPauseAutoplay()
            ) {
              return;
            }

            goTo(
              activeIndex + 1,
              {
                restartAutoplay:
                  false,
              }
            );
          },
          autoplayDuration
        );
    }


    /* =========================================
       AUTOPLAY CONTROL
       ========================================= */

    function updateAutoplayButton() {
      if (!autoplayButton) {
        return;
      }

      if (reducedMotion) {
        autoplayButton.disabled =
          true;

        autoplayButton.setAttribute(
          'aria-pressed',
          'true'
        );

        autoplayButton.setAttribute(
          'aria-label',
          'Testimonial autoplay disabled because reduced motion is enabled'
        );

        return;
      }

      autoplayButton.disabled =
        !autoplayEnabled;

      autoplayButton.setAttribute(
        'aria-pressed',
        autoplayPausedByUser
          ? 'true'
          : 'false'
      );

      autoplayButton.setAttribute(
        'aria-label',
        autoplayPausedByUser
          ? 'Resume testimonial autoplay'
          : 'Pause testimonial autoplay'
      );
    }

    function toggleAutoplay() {
      if (
        !autoplayEnabled ||
        reducedMotion
      ) {
        return;
      }

      autoplayPausedByUser =
        !autoplayPausedByUser;

      updateAutoplayButton();

      if (autoplayPausedByUser) {
        clearAutoplayTimer();
      } else {
        scheduleAutoplay();
      }
    }

    updateAutoplayButton();


    /* =========================================
       TRANSITION COMPLETION
       ========================================= */

    function completeTransition(
      previousIndex,
      nextIndex
    ) {
      setSlideState(
        previousIndex,
        false
      );

      setSlideState(
        nextIndex,
        true
      );

      activeIndex =
        nextIndex;

      updateCounter();

      isAnimating = false;

      transitionTimeline = null;

      scheduleAutoplay();
    }


    /* =========================================
       SLIDE NAVIGATION
       ========================================= */

    function goTo(
      nextIndex,
      options = {}
    ) {
      if (
        isAnimating ||
        slides.length < 2
      ) {
        return;
      }

      const normalizedIndex =
        (
          (
            nextIndex %
            slides.length
          ) +
          slides.length
        ) %
        slides.length;

      if (
        normalizedIndex ===
        activeIndex
      ) {
        if (
          options.restartAutoplay !==
          false
        ) {
          scheduleAutoplay();
        }

        return;
      }

      clearAutoplayTimer();

      const previousIndex =
        activeIndex;

      if (reducedMotion) {
        setSlideState(
          previousIndex,
          false
        );

        setSlideState(
          normalizedIndex,
          true
        );

        activeIndex =
          normalizedIndex;

        updateCounter();

        scheduleAutoplay();

        return;
      }

      isAnimating = true;

      const outgoingSlide =
        slides[
          previousIndex
        ];

      const incomingSlide =
        slides[
          normalizedIndex
        ];

      transitionTimeline?.kill();

      const outgoingLines =
        outgoingSlide.getLines();

      const incomingLines =
        incomingSlide.getLines();

      gsap.set(
        incomingSlide.item,
        {
          autoAlpha: 1,
          pointerEvents:
            'none',
        }
      );

      if (incomingLines.length) {
        gsap.set(
          incomingLines,
          {
            yPercent: 110,
          }
        );
      }

      if (
        outgoingSlide.image
      ) {
        gsap.set(
          outgoingSlide.image,
          {
            clipPath:
              IMAGE_CLIP_VISIBLE,
          }
        );
      }

      transitionTimeline =
        gsap.timeline({
          onComplete() {
            completeTransition(
              previousIndex,
              normalizedIndex
            );
          },
        });

      if (outgoingLines.length) {
        transitionTimeline.to(
          outgoingLines,
          {
            yPercent: -110,

            duration: 0.6,

            ease:
              'power4.inOut',

            stagger: {
              amount: 0.25,
            },
          },
          0
        );
      }

      if (
        outgoingSlide.image
      ) {
        transitionTimeline.to(
          outgoingSlide.image,
          {
            clipPath:
              IMAGE_CLIP_HIDDEN,

            duration: 0.6,

            ease:
              'power4.inOut',
          },
          0
        );
      }

      if (incomingLines.length) {
        transitionTimeline.to(
          incomingLines,
          {
            yPercent: 0,

            duration: 0.7,

            ease:
              'power4.inOut',

            stagger: {
              amount: 0.4,
            },
          },
          outgoingLines.length
            ? '>-0.3'
            : 0
        );
      }

      if (
        incomingSlide.image
      ) {
        transitionTimeline.fromTo(
          incomingSlide.image,
          {
            clipPath:
              IMAGE_CLIP_HIDDEN,
          },
          {
            clipPath:
              IMAGE_CLIP_VISIBLE,

            duration: 0.75,

            ease:
              'power4.inOut',
          },
          incomingLines.length
            ? '<'
            : 0
        );
      }

      if (
        !outgoingLines.length &&
        !incomingLines.length &&
        !outgoingSlide.image &&
        !incomingSlide.image
      ) {
        transitionTimeline.to(
          {},
          {
            duration: 0.01,
          }
        );
      }

      transitionTimeline.set(
        outgoingSlide.item,
        {
          autoAlpha: 0,
        }
      );
    }


    /* =========================================
       MANUAL CONTROLS
       ========================================= */

    nextButton?.addEventListener(
      'click',
      () => {
        goTo(
          activeIndex + 1
        );
      },
      {
        signal,
      }
    );

    previousButton?.addEventListener(
      'click',
      () => {
        goTo(
          activeIndex - 1
        );
      },
      {
        signal,
      }
    );

    autoplayButton?.addEventListener(
      'click',
      toggleAutoplay,
      {
        signal,
      }
    );


    /* =========================================
       KEYBOARD
       ========================================= */

    root.addEventListener(
      'keydown',
      (event) => {
        const target =
          event.target;

        const isTypingTarget =
          target &&
          (
            target.tagName ===
              'INPUT' ||
            target.tagName ===
              'TEXTAREA' ||
            target.tagName ===
              'SELECT' ||
            target.isContentEditable
          );

        if (
          isTypingTarget ||
          event.altKey ||
          event.ctrlKey ||
          event.metaKey
        ) {
          return;
        }

        if (
          event.key ===
          'ArrowRight'
        ) {
          event.preventDefault();

          goTo(
            activeIndex + 1
          );

          return;
        }

        if (
          event.key ===
          'ArrowLeft'
        ) {
          event.preventDefault();

          goTo(
            activeIndex - 1
          );

          return;
        }

        if (
          event.key ===
          'Home'
        ) {
          event.preventDefault();

          goTo(0);

          return;
        }

        if (
          event.key ===
          'End'
        ) {
          event.preventDefault();

          goTo(
            slides.length - 1
          );
        }
      },
      {
        signal,
      }
    );


    /* =========================================
       DOCUMENT VISIBILITY
       ========================================= */

    document.addEventListener(
      'visibilitychange',
      () => {
        documentHidden =
          document.hidden;

        if (documentHidden) {
          clearAutoplayTimer();
        } else {
          scheduleAutoplay();
        }
      },
      {
        signal,
      }
    );


    /* =========================================
       VIEWPORT VISIBILITY
       ========================================= */

    if (
      'IntersectionObserver' in
      window
    ) {
      intersectionObserver =
        new IntersectionObserver(
          (entries) => {
            const entry =
              entries[0];

            if (!entry) {
              return;
            }

            isInView =
              entry.isIntersecting;

            if (isInView) {
              scheduleAutoplay();
            } else {
              clearAutoplayTimer();
            }
          },
          {
            threshold: 0.01,
          }
        );

      intersectionObserver.observe(
        root
      );
    }


    /* =========================================
       INITIAL AUTOPLAY
       ========================================= */

    scheduleAutoplay();


    /* =========================================
       INSTANCE
       ========================================= */

    const instance = {
      root,

      goToIndex(index) {
        goTo(index);
      },

      destroy() {
        if (destroyed) {
          return;
        }

        destroyed = true;

        controller.abort();

        clearAutoplayTimer();

        intersectionObserver
          ?.disconnect();

        intersectionObserver =
          null;

        if (
          transitionTimeline
        ) {
          transitionTimeline.kill();

          transitionTimeline =
            null;
        }

        slides.forEach(
          (slide) => {
            slide
              .splitInstances
              .forEach(
                (
                  splitInstance
                ) => {
                  if (
                    splitInstance &&
                    typeof splitInstance
                      .revert ===
                      'function'
                  ) {
                    splitInstance
                      .revert();
                  }
                }
              );
          }
        );

        delete root
          ._wamboltTestimonials;

        delete root
          ._wamboltTestimonialDestroy;

        delete root
          ._wamboltTestimonialAbort;
      },
    };

    root._wamboltTestimonials =
      instance;

    root._wamboltTestimonialDestroy =
      instance.destroy;

    root._wamboltTestimonialAbort =
      controller;

    return instance;
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initTestimonials(
    scope = document
  ) {
    const roots =
      getRoots(scope);

    if (!roots.length) {
      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    roots.forEach(
      createTestimonials
    );

    return true;
  }

  function destroyTestimonials(
    scope = document
  ) {
    getRoots(scope).forEach(
      (root) => {
        const instance =
          root
            ._wamboltTestimonials;

        if (
          instance &&
          typeof instance.destroy ===
            'function'
        ) {
          instance.destroy();

          return;
        }

        if (
          typeof root
            ._wamboltTestimonialDestroy ===
            'function'
        ) {
          root
            ._wamboltTestimonialDestroy();
        }
      }
    );
  }

  function rebuildTestimonials() {
    destroyTestimonials(
      document
    );

    if (
      !initTestimonials(
        document
      )
    ) {
      scheduleDependencyRetry();
    }
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (
      dependencyRetryTimer === null
    ) {
      return;
    }

    window.clearTimeout(
      dependencyRetryTimer
    );

    dependencyRetryTimer =
      null;
  }

  function scheduleDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Testimonials could not initialize because GSAP or SplitText is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer =
            null;

          if (
            initTestimonials(
              document
            )
          ) {
            dependencyRetryAttempts =
              0;

            clearDependencyRetry();

            return;
          }

          scheduleDependencyRetry();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  function start() {
    if (
      initTestimonials(
        document
      )
    ) {
      dependencyRetryAttempts =
        0;

      clearDependencyRetry();

      return;
    }

    scheduleDependencyRetry();
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     REDUCED MOTION CHANGES
     ========================================= */

  if (
    typeof reducedMotionQuery
      .addEventListener ===
    'function'
  ) {
    reducedMotionQuery
      .addEventListener(
        'change',
        rebuildTestimonials
      );
  } else if (
    typeof reducedMotionQuery
      .addListener ===
    'function'
  ) {
    reducedMotionQuery
      .addListener(
        rebuildTestimonials
      );
  }


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        !initTestimonials(
          event.target
        )
      ) {
        scheduleDependencyRetry();
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyTestimonials(
        event.target
      );
    }
  );


  /* =========================================
     SHOPIFY BLOCK SELECTION
     ========================================= */

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const item =
        event.target.closest?.(
          ITEM_SELECTOR
        );

      const root =
        event.target.closest?.(
          ROOT_SELECTOR
        );

      if (
        !item ||
        !root
      ) {
        return;
      }

      const items =
        Array.from(
          root.querySelectorAll(
            ITEM_SELECTOR
          )
        );

      const index =
        items.indexOf(item);

      if (index < 0) {
        return;
      }

      root
        ._wamboltTestimonials
        ?.goToIndex(index);
    }
  );
})();